import React, { FormEvent, useCallback, useState } from 'react';

import Input from 'components/input/input';
import FormattedMessage from 'components/formatted-message';
import PropsFormatter, { tr } from 'containers/props-formatter';
import * as endpoints from 'utils/api/endpoints';

import { AuthContainer, AuthError, AuthForm, AuthHeader, AuthSubmitButton, AuthText } from '../components/auth-layout';
import { AuthOptionLinks, LOGIN_AUTH_LINK, SIGN_UP_AUTH_LINK } from '../auth-option-links';
import { isUsernameFilled } from '../helpers/auth-form';
import { usePublicPost } from '../hooks/use-public-post';

const AUTH_LINKS = [SIGN_UP_AUTH_LINK, LOGIN_AUTH_LINK] as const;

/** PublicApiController.UnblockRequestDto */
type UnblockRequestDto = { username: string };

/**
 * /request-unlock: POST /public/users/unblock-request. The backend always answers 201 (it does not tell whether the
 * account exists or why it is locked) and sends the link to /unblock-account only when the user may unlock it.
 */
const RequestUnlock = () => {
    const [username, setUsername] = useState('');
    const [validationMode, setValidationMode] = useState(false);
    const [body, setBody] = useState<UnblockRequestDto | null>(null);

    const { status } = usePublicPost({ url: endpoints.unblockRequest, body });
    const submitEnabled = isUsernameFilled(username);

    const handleSubmit = useCallback(
        (event: FormEvent) => {
            event.preventDefault();

            if (!submitEnabled) {
                setValidationMode(true);

                return;
            }

            setBody({ username: username.trim() });
        },
        [username, submitEnabled]
    );

    return (
        <AuthContainer>
            <AuthForm onSubmit={handleSubmit}>
                <AuthHeader>
                    <FormattedMessage id="requestUnlock.title" />
                </AuthHeader>

                {status === 'success' ? (
                    <AuthText>
                        <FormattedMessage id="requestUnlock.successNotification" />
                    </AuthText>
                ) : (
                    <>
                        <PropsFormatter
                            validationPredicate={validationMode && isUsernameFilled}
                            placeholder={tr('requestUnlock.username')}
                        >
                            <Input
                                type="text"
                                value={username}
                                onChange={event => setUsername(event.target.value)}
                                unbounded
                            />
                        </PropsFormatter>

                        <AuthSubmitButton
                            type="submit"
                            disabled={status === 'pending' || (validationMode && !submitEnabled)}
                            isLoading={status === 'pending'}
                        >
                            <FormattedMessage id="requestUnlock.button" />
                        </AuthSubmitButton>
                    </>
                )}

                {status === 'error' && (
                    <AuthError>
                        <FormattedMessage id="requestUnlock.error" />
                    </AuthError>
                )}
            </AuthForm>
            <AuthOptionLinks linksToShow={AUTH_LINKS} />
        </AuthContainer>
    );
};

export default RequestUnlock;
